<template>
  <div>
    <div class="pdf-labels-content">
      <div class="action-toolbar">
        <div class="action-buttons">
          <!-- Input file oculto -->
          <input 
            id="pdfFileUpload" 
            ref="fileInput" 
            type="file" 
            accept=".pdf" 
            style="display: none" 
            @change="uploadPDF" 
          />
          
          <argon-button
            v-if="!hasPdfTemplate && !hasServerTemplate"
            color="info"
            variant="gradient"
            class="m-2"
            @click="$refs.fileInput.click()"
          >
            <i class="fas fa-upload me-2"></i>
            Sube un PDF usando el área de abajo
          </argon-button>

          <argon-button
            v-if="!hasPdfTemplate && hasServerTemplate"
            color="success"
            variant="gradient"
            class="m-2"
            :disabled="templateLoading"
            @click="createDefaultTemplate"
          >
            <i class="fas me-2" :class="templateLoading ? 'fa-spinner fa-spin' : 'fa-magic'"></i>
            {{ templateLoading ? 'Cargando PDF...' : 'Cargar PDF Existente' }}
          </argon-button>

          <argon-button
            v-if="hasPdfTemplate"
            color="primary"
            variant="gradient"
            class="m-2"
            @click="generatePDF(realPrescriptionData)"
          >
            <i class="fas fa-file-pdf me-2"></i>
            Generar PDF
          </argon-button>
          <argon-button v-if="hasPdfTemplate" color="primary" variant="gradient" class="m-2" @click="handleSave">
            <i class="fas fa-save me-2"></i>
            Guardar cambios
          </argon-button>
          <argon-button
            v-if="hasPdfTemplate"
            color="secondary"
            variant="gradient"
            class="m-2"
            @click="forceOverrideDefaults"
          >
            <i class="fas fa-redo me-2"></i>
            Cargar parámetros por defecto
          </argon-button>

          <argon-button
            v-if="hasPdfTemplate"
            color="primary"
            variant="gradient"
            class="m-2"
            size="sm"
            @click="showManageModal = true"
          >
            <i class="fas fa-layer-group me-2"></i>
            Gestionar labels
          </argon-button>
        </div>
      </div>

      <!-- Mensaje informativo si no hay plantilla -->
      <div v-if="!hasPdfTemplate && !hasServerTemplate" class="info-card upload-info">
        <div class="info-icon">
          <i class="fas fa-cloud-upload-alt"></i>
        </div>
        <div class="info-content">
          <h4 class="info-title">Sube un PDF usando el área de abajo</h4>
          <p class="info-description">Para poder crear prescripciones, necesitas subir un archivo PDF que servirá como plantilla. Usa el área de "Arrastra y suelta" que está abajo para subir tu plantilla.</p>
        </div>
      </div>

      <div v-if="!hasPdfTemplate && hasServerTemplate" class="info-card success-info">
        <div class="info-icon success">
          <i class="fas fa-check-circle"></i>
        </div>
        <div class="info-content">
          <h4 class="info-title">PDF encontrado</h4>
          <p class="info-description">Se encontró una plantilla PDF en tu perfil de doctor. Haz clic en "Cargar PDF Existente" para visualizarla y configurar las etiquetas.</p>
        </div>
      </div>

      <!-- Área de drag and drop para subir PDF -->
      <div
        v-if="!hasPdfTemplate"
        class="upload-container" 
        :class="{ 'dragover': isDragging }" 
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false" 
        @drop="handleDropWithState"
      >
        <input id="fileUpload" type="file" accept=".pdf" class="file-input" @change="uploadPDF" />
        <label for="fileUpload" class="upload-label">
          <div class="upload-content">
            <i class="fas fa-cloud-upload-alt upload-icon" :class="{ 'text-success': isDragging }"></i>
            <div class="upload-text">
              <strong>{{ isDragging ? '¡Suelta el archivo aquí!' : 'Arrastra y suelta tu PDF aquí' }}</strong>
              <p v-if="!isDragging">o haz clic para seleccionar un archivo</p>
              <p v-else class="text-success">Archivo listo para subir</p>
            </div>
            <div v-if="selectedFile" class="selected-file">
              <i class="fas fa-file-pdf me-2"></i>
              {{ selectedFile.name }}
            </div>
          </div>
        </label>
      </div>

      <div class="pdf-workspace">
        <!-- Contenedor del PDF con escala responsiva -->
        <div class="pdf-viewer-container">
          <!-- Información de escala (pequeña, en la esquina) -->
          <div v-if="hasPdfTemplate" class="scale-info">
            <small>Escala: {{ scaleInfo.scale }}x ({{ scaleInfo.scaledWidth }}×{{ scaleInfo.scaledHeight }})</small>
          </div>
          <div ref="pdfContainer" class="pdf-viewer">
            <canvas ref="pdfCanvas" width="861" height="1115"></canvas>

            <!-- Cuadrícula superpuesta (opcional) -->
            <div class="grid-overlay">
              <template v-for="(row, rowIndex) in 25" :key="'row-'+rowIndex">
                <template v-for="(col, colIndex) in 20" :key="'col-'+colIndex">
                  <div></div>
                </template>
              </template>
            </div>

            <!-- Solo mostrar campos que están dentro de la cuadrícula -->
            <template v-for="(label) in (pdfData?.labels || [])" :key="label.id">
              <span
v-if="!label.isInput" class="label" :data-label-id="label.id" :class="{ selected: selectedLabelId === label.id }"
                :style="{ top: label.y + 'px', left: label.x + 'px' }"
                @mousedown="(e) => handleLabelMouseDown(e, label)">
                {{ label.text }}
              </span>
              <div
v-else :id="label.id" class="field" :data-label-id="label.id" :class="{ selected: selectedLabelId === label.id }" contenteditable="true" :style="{
                top: label.y + 'px',
                left: label.x + 'px',
                width: label.width + 'px',
                height: label.height + 'px'
              }" @mousedown="(e) => handleLabelMouseDown(e, label)" @mouseup="() => handleLabelMouseUp(label)" v-html="label.value"></div>
            </template>

            <div
v-if="selectedLabel"
              class="label-actions-floating" :style="labelActionStyle" @mousedown.stop @click.stop>
              <button class="close-chip" title="Eliminar label" @click="requestDeleteLabel(selectedLabel)">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>

      </div>

      <!-- Área de drag and drop para volver a cargar PDF -->
      <div
        v-if="hasPdfTemplate"
        class="upload-container" 
        :class="{ 'dragover': isDragging }" 
        @dragover.prevent="isDragging = true"
        @dragleave="isDragging = false" 
        @drop="handleDropWithState"
      >
        <input id="fileUploadReplace" type="file" accept=".pdf" class="file-input" @change="uploadPDF" />
        <label for="fileUploadReplace" class="upload-label">
          <div class="upload-content">
            <i class="fas fa-cloud-upload-alt upload-icon" :class="{ 'text-success': isDragging }"></i>
            <div class="upload-text">
              <strong>{{ isDragging ? '¡Suelta el archivo aquí!' : 'Volver a cargar PDF' }}</strong>
              <p v-if="!isDragging">Arrastra y suelta un nuevo archivo PDF aquí o haz clic para seleccionar</p>
              <p v-else class="text-success">Archivo listo para subir</p>
            </div>
            <div v-if="selectedFile" class="selected-file">
              <i class="fas fa-file-pdf me-2"></i>
              {{ selectedFile.name }}
            </div>
          </div>
        </label>
      </div>
    </div>

    <!-- Modal para eliminar labels -->
    <BaseModal
      v-model="showManageModal"
      title="Gestionar labels"
      icon="fas fa-layer-group text-primary"
      size="large"
      :body-max-height="'70vh'"
    >
      <div class="manage-tabs">
        <button
          class="tab-btn"
          :class="{ active: manageTab === 'inside' }"
          @click="manageTab = 'inside'"
        >
          En el PDF <span class="pill">{{ fieldsInsideGrid.length }}</span>
        </button>
        <button
          class="tab-btn"
          :class="{ active: manageTab === 'outside' }"
          @click="manageTab = 'outside'"
        >
          Fuera del PDF <span class="pill warning">{{ fieldsOutsideGrid.length }}</span>
        </button>
        <button
          class="tab-btn"
          :class="{ active: manageTab === 'trash' }"
          @click="manageTab = 'trash'"
        >
          Papelera <span class="pill danger">{{ deletedLabels.length }}</span>
        </button>
      </div>

      <div class="manage-section">
        <div class="section-header">
          <div>
            <h6 v-if="manageTab === 'inside'">En el PDF</h6>
            <h6 v-else-if="manageTab === 'outside'">Fuera del PDF</h6>
            <h6 v-else>Papelera</h6>
            <small class="text-muted">
              <template v-if="manageTab === 'inside'">Arrastra en el lienzo o envía a la papelera</template>
              <template v-else-if="manageTab === 'outside'">Incluye al PDF o envía a la papelera</template>
              <template v-else>Restaura o elimina definitivamente</template>
            </small>
          </div>
        </div>

        <div v-if="currentManageList.length === 0" class="empty-row">
          <span v-if="manageTab === 'inside'">Sin labels en el PDF</span>
          <span v-else-if="manageTab === 'outside'">No hay campos fuera</span>
          <span v-else>Papelera vacía</span>
        </div>

        <div v-else class="manage-list simple-scroll">
          <div
            v-for="label in currentManageList"
            :key="`${manageTab}-${label.id}`"
            class="manage-item"
          >
            <div class="manage-meta">
              <strong>{{ label.id }}</strong>
              <small class="text-muted">
                <template v-if="manageTab !== 'trash'">({{ label.x }}, {{ label.y }})</template>
                <template v-else>{{ formatDeletedDate(label.deletedAt) }}</template>
              </small>
            </div>
            <div class="manage-actions">
              <template v-if="manageTab === 'inside'">
                <button class="btn btn-sm btn-outline-danger" @click="requestDeleteLabel(label)">
                  <i class="fas fa-trash"></i>
                </button>
              </template>
              <template v-else-if="manageTab === 'outside'">
                <button class="btn btn-sm btn-outline-success" @click="moveToGrid(label)">
                  <i class="fas fa-arrow-left"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="requestDeleteLabel(label)">
                  <i class="fas fa-trash"></i>
                </button>
              </template>
              <template v-else>
                <button class="btn btn-sm btn-outline-success" @click="restoreLabel(label)">
                  <i class="fas fa-undo"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" @click="permanentDelete(label)">
                  <i class="fas fa-times"></i>
                </button>
              </template>
            </div>
          </div>
        </div>
      </div>

      <template #footer>
        <button class="modal-btn-close" @click="showManageModal = false">
          <i class="fas fa-times"></i>
          Cerrar
        </button>
      </template>
    </BaseModal>

    <!-- Modal para eliminar labels -->
    <BaseModal
      v-model="showDeleteModal"
      title="Eliminar labels"
      icon="fas fa-trash-alt text-danger"
      size="large"
      :body-max-height="'70vh'"
    >
      <div class="alert alert-warning mb-3">
        <i class="fas fa-exclamation-triangle me-2"></i>
        <strong>¡Atención!</strong> Los labels eliminados se enviarán a la papelera y podrás restaurarlos después.
      </div>

      <div class="labels-list">
        <div class="label-selection-header">
          <label class="checkbox-label">
            <input v-model="selectAllLabels" type="checkbox" @change="toggleAllLabels" />
            <span>Seleccionar todos</span>
          </label>
          <small class="text-muted">{{ selectedLabelsForDelete.length }} seleccionados</small>
        </div>

        <div class="labels-grid">
          <div
v-for="label in (pdfData?.labels || [])" :key="label.id" class="label-item"
            :class="{ 'selected': selectedLabelsForDelete.includes(label.id) }">
            <label class="label-checkbox">
              <input v-model="selectedLabelsForDelete" type="checkbox" :value="label.id" />
              <div class="label-info">
                <div class="label-name">
                  <i
:class="label.isInput ? 'fas fa-edit text-primary' : 'fas fa-tag text-secondary'"
                    class="me-2"></i>
                  <strong>{{ label.id }}</strong>
                </div>
                <div class="label-details">
                  <span class="label-text">{{ label.text || 'Sin texto' }}</span>
                  <span class="label-position">({{ label.x }}, {{ label.y }})</span>
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>

      <template #footer>
        <button class="btn-secondary" @click="showDeleteModal = false">
          <i class="fas fa-times me-2"></i>Cancelar
        </button>
        <button
          class="btn-danger"
          :disabled="selectedLabelsForDelete.length === 0"
          @click="deleteSelectedLabels"
        >
          <i class="fas fa-trash me-2"></i>
          Eliminar {{ selectedLabelsForDelete.length }} label(s)
        </button>
      </template>
    </BaseModal>

    <!-- Modal de papelera (labels eliminados) -->
    <BaseModal
      v-model="showTrashModal"
      title="Papelera de labels"
      icon="fas fa-trash-restore text-danger"
      size="large"
      :body-max-height="'70vh'"
    >
      <div class="trash-stats">
        <div class="stat-item">
          <i class="fas fa-trash text-muted"></i>
          <span>{{ deletedLabels.length }} elementos</span>
        </div>
        <div class="stat-item">
          <i class="fas fa-clock text-muted"></i>
          <span>Última eliminación: {{ lastDeletedDate }}</span>
        </div>
      </div>

      <div v-if="deletedLabels.length === 0" class="empty-trash">
        <i class="fas fa-trash fa-3x text-muted mb-3"></i>
        <h4>La papelera está vacía</h4>
        <p class="text-muted">No hay labels eliminados para restaurar</p>
      </div>

      <div v-else class="deleted-labels-list">
        <div class="deleted-selection-header">
          <label class="checkbox-label">
            <input v-model="selectAllDeleted" type="checkbox" @change="toggleAllDeleted" />
            <span>Seleccionar todos</span>
          </label>
          <div class="action-buttons">
            <button
class="btn btn-sm btn-success me-2" :disabled="selectedDeletedLabels.length === 0"
              @click="restoreSelectedLabels">
              <i class="fas fa-undo me-1"></i>
              Restaurar {{ selectedDeletedLabels.length > 0 ? selectedDeletedLabels.length : '' }}
            </button>
            <button
class="btn btn-sm btn-danger" :disabled="selectedDeletedLabels.length === 0"
              @click="permanentDeleteSelected">
              <i class="fas fa-times me-1"></i>
              Eliminar permanentemente
            </button>
          </div>
        </div>

        <div class="deleted-labels-grid">
          <div
v-for="deletedLabel in deletedLabels" :key="'deleted-' + deletedLabel.id" class="deleted-label-item"
            :class="{ 'selected': selectedDeletedLabels.includes(deletedLabel.id) }">
            <label class="deleted-label-checkbox">
              <input v-model="selectedDeletedLabels" type="checkbox" :value="deletedLabel.id" />
              <div class="deleted-label-info">
                <div class="deleted-label-header">
                  <div class="label-name">
                    <i
:class="deletedLabel.isInput ? 'fas fa-edit text-primary' : 'fas fa-tag text-secondary'"
                      class="me-2"></i>
                    <strong>{{ deletedLabel.id }}</strong>
                  </div>
                  <div class="deleted-date">
                    <i class="fas fa-clock me-1"></i>
                    <small>{{ formatDeletedDate(deletedLabel.deletedAt) }}</small>
                  </div>
                </div>
                <div class="deleted-label-details">
                  <span class="label-text">{{ deletedLabel.text || 'Sin texto' }}</span>
                  <span class="label-position">({{ deletedLabel.x }}, {{ deletedLabel.y }})</span>
                </div>
                <div class="deleted-label-actions">
                  <button class="btn btn-sm btn-outline-success me-2" @click="restoreLabel(deletedLabel)">
                    <i class="fas fa-undo me-1"></i>Restaurar
                  </button>
                  <button class="btn btn-sm btn-outline-danger" @click="permanentDelete(deletedLabel)">
                    <i class="fas fa-times me-1"></i>Eliminar
                  </button>
                </div>
              </div>
            </label>
          </div>
        </div>
      </div>

      <template #footer>
        <button class="btn-secondary" @click="showTrashModal = false">
          <i class="fas fa-times me-2"></i>Cerrar
        </button>
        <button v-if="deletedLabels.length > 0" class="btn-danger" @click="clearTrash">
          <i class="fas fa-broom me-2"></i>Vaciar papelera
        </button>
      </template>
    </BaseModal>
  </div>
</template>

<script setup>
import { getCurrentInstance, computed, ref, watch } from "vue";
import { useStore } from "vuex";
import { usePdfDesigner } from "@/composables/usePdfDesigner";
import { usePrescriptionTemplates } from "@/composables/usePrescriptionTemplates";
import { useActions } from "@/composables/useActions";
import ArgonButton from '@/components/ArgonButton.vue';
import BaseModal from '@/components/BaseModal.vue';

import api from "@/services/api";
const { proxy } = getCurrentInstance();
const store = useStore();

// Estado para el drag and drop
const isDragging = ref(false);

// Estado para modales de gestión de labels
const showDeleteModal = ref(false);
const showTrashModal = ref(false);
const showManageModal = ref(false);

// Estado para selección de labels
const selectedLabelsForDelete = ref([]);
const selectedDeletedLabels = ref([]);
const selectAllLabels = ref(false);
const selectAllDeleted = ref(false);

// Estado para labels eliminados (papelera)
const deletedLabels = ref([]);

// Composable para manejo de plantillas
const {
  isLoading: templateLoading
} = usePrescriptionTemplates();

// Props para recibir los datos desde el componente padre
const props = defineProps({
  prescription: {
    type: Object,
    default: () => ({})
  },
  doctor: {
    type: Object,
    default: () => ({})
  }
});

// Computed para obtener los datos reales del usuario/doctor
const realPrescriptionData = computed(() => {
  const user = store.state.user || {};
  return {
    id: props.prescription.id || '1', // Fallback
    doctor: user.doctor?.id || props.doctor.id || '1' // Usar el ID del doctor logueado
  };
});

// Constantes para los límites de la cuadrícula (formato Letter proporcional)
const GRID_WIDTH = 861;
const GRID_HEIGHT = 1115;

// Función para verificar si un campo está dentro de la cuadrícula
const isFieldInsideGrid = (label) => {
  const fieldWidth = label.width || 100;
  const fieldHeight = label.height || 20;

  const isInside = label.x >= 0 &&
    label.y >= 0 &&
    label.x + fieldWidth <= GRID_WIDTH &&
    label.y + fieldHeight <= GRID_HEIGHT;

  return isInside;
};

const { showRecord, editModel } = useActions('doctors', {
  showRecord: () => {
    // Mostrando registro
  },
  editModel: () => {
    // Editando modelo
  }
});

const resourceApi = "doctors";
const resourceId = computed(() => {
  const user = store.state.user || {};
  return user.doctor?.id || props.doctor.id || 1;
});

const defaultLabels = [
  { id: "paciente", text: "Paciente", x: 58, y: 105, page: 1, value: "" },
  {
    id: "nombre",
    text: "",
    x: 58, y: 122,
    isInput: true,
    page: 1,
    value: "Victor Osornio",
    width: 200,
    height: 50
  },
  { id: "edad", text: "Edad", x: 336, y: 105, page: 1, value: "" },
  { id: "edad_valor", text: "18", x: 336, y: 122, page: 1, value: "" },

  { id: "fecha", text: "Fecha", x: 655, y: 105, page: 1, value: "" },
  { id: "fecha_valor", text: "22/03/1986", x: 653, y: 122, page: 1, value: "" },

  { id: "ta", text: "T/A:", x: 58, y: 148, page: 1, value: "" },
  { id: "ta_valor", text: "129/182", x: 85, y: 148, page: 1, value: "" },

  { id: "peso", text: "Peso:", x: 140, y: 148, page: 1, value: "" },
  { id: "peso_valor", text: "86", x: 175, y: 148, page: 1, value: "" },

  { id: "talla", text: "Talla:", x: 235, y: 148, page: 1, value: "" },
  { id: "talla_valor", text: "1.86", x: 273, y: 148, page: 1, value: "" },

  { id: "temp", text: "Temp:", x: 309, y: 148, page: 1, value: "" },
  { id: "temp_valor", text: "37.1", x: 350, y: 148, page: 1, value: "" },

  { id: "imc", text: "IMC:", x: 390, y: 148, page: 1, value: "" },
  { id: "imc_valor", text: "26.4", x: 421, y: 148, page: 1, value: "" },

  { id: "fc", text: "FC:", x: 452, y: 148, page: 1, value: "" },
  { id: "fc_valor", text: "12", x: 471, y: 148, page: 1, value: "" },

  { id: "fr", text: "FR:", x: 518, y: 148, page: 1, value: "" },
  { id: "fr_valor", text: "44", x: 539, y: 148, page: 1, value: "" },

  { id: "sato2", text: "SatO2:", x: 584, y: 148, page: 1, value: "" },
  { id: "sato2_valor", text: "423", x: 625, y: 148, page: 1, value: "" },

  // Campos que estarán fuera de la cuadrícula (posiciones > 800px)
  { id: "dx", text: "Diagnóstico:", x: 820, y: 180, page: 1, value: "" },
  {
    id: "dx_valor",
    text: "", x: 820, y: 200,
    isInput: true,
    page: 1, value: "Ninguno",
    width: 200,
    height: 50
  },

  { id: "cita", text: "Próxima cita:", x: 820, y: 260, page: 1, value: "" },
  { id: "cita_valor", text: "07/07/2025", x: 820, y: 280, page: 1, value: "" },

  {
    id: "medicamentos",
    text: "Medicamentos",
    x: 57,
    y: 192,
    isInput: true,
    page: 1,
    value: "<strong>1.-NEUROFLAX ...</strong><br><br>",
    width: 200,
    height: 50
  },

  // Campo adicional fuera de la cuadrícula
  {
    id: "notas_privadas",
    text: "Notas Privadas",
    x: 820,
    y: 340,
    isInput: true,
    page: 1,
    value: "Notas que no aparecerán en el PDF",
    width: 250,
    height: 80
  }
];

async function generatePdfMethod(prescriptionPayload = {}) {
  const formData = new FormData();

  // Usar los datos reales del usuario logueado
  const prescriptionData = {
    ...realPrescriptionData.value,
    ...prescriptionPayload
  };

  formData.append("prescription", JSON.stringify(prescriptionData));

  try {
    const response = await api.post("generate-labeled-pdf", formData);
    if (response && response.status === 200) {
      window.open(response.data.data.pdf_url, "_blank");
      proxy.$notify(response.data.message, "", "success", "ni ni-like-2 ni-lg");
    } else {
      proxy.$notify(response.data.message, "", "danger", "ni ni-fat-remove ni-lg");
    }
  } catch (error) {
    if (error.response && error.response.data && error.response.data.message) {
      proxy.$notify(error.response.data.message, "", "danger", "ni ni-fat-remove ni-lg");
    } else {
      // Error desconocido al generar generate-labeled-pdf
      proxy.$notify("Error al generar el PDF. Verifica que tengas un perfil de doctor configurado.", "", "danger", "ni ni-fat-remove ni-lg");
    }
  }
}

function notify() {
  // Notificación
}

// Instanciamos el composable
const {
  pdfContainer,
  pdfCanvas,
  pdfData,
  selectedFile,
  visualScale,
  containerWidth,
  containerHeight,
  loadDefaultPdfData,
  handleDrop,
  uploadPDF,
  moveLabel,
  updateSize,
  handleSave,
  generatePDF,
} = usePdfDesigner({
  resourceApi,
  resourceId: resourceId.value,
  showRecord,
  editModel,
  defaultLabels,
  saveValues: true,
  localStorageKey: "pdfData-doctor",
  pdfEntyKey: null, // Los datos del doctor están directamente en el objeto user
  pdfParametersKey: "prescription_pdf_parameters",
  pdfUrlKey: "prescription_pdf_url", // Usar la clave correcta
  generatePdfMethod,
  notify,
  // Función personalizada para filtrar campos al guardar
  filterLabelsForSave: (labels) => {
    // Obtener IDs de labels eliminados
    const deletedIds = deletedLabels.value.map(label => label.id);
    // Filtrar labels que están dentro de la cuadrícula Y no están eliminados
    return labels.filter(label => isFieldInsideGrid(label) && !deletedIds.includes(label.id));
  },
  // Pasar los datos del usuario directamente desde el store
  userData: computed(() => store.state.user),
  // Pasar el servicio API
  apiService: api,
  // Función para actualizar el store
  updateUserStore: (userData) => store.dispatch('updateUser', userData)
});

const hasServerTemplate = computed(() => Boolean(store.state.user?.doctor?.prescription_pdf_url));
const hasPdfTemplate = computed(() => Boolean(pdfData.value?.url));
// eslint-disable-next-line no-unused-vars
const labelsCount = computed(() => pdfData.value?.labels?.length || 0);
const selectedLabelId = ref(null);
const selectedLabel = computed(() => (pdfData.value?.labels || []).find(l => l.id === selectedLabelId.value));
const manageTab = ref('inside');

// Computed para campos dentro de la cuadrícula - DEBE estar después del composable
const fieldsInsideGrid = computed(() => {
  if (!pdfData?.value?.labels || !Array.isArray(pdfData.value.labels)) {
    return [];
  }
  const inside = pdfData.value.labels.filter(isFieldInsideGrid);
  return inside;
});

// Computed para campos fuera de la cuadrícula - DEBE estar después del composable
const fieldsOutsideGrid = computed(() => {
  if (!pdfData?.value?.labels || !Array.isArray(pdfData.value.labels)) {
    return [];
  }
  const outside = pdfData.value.labels.filter(label => !isFieldInsideGrid(label));
  return outside;
});

// eslint-disable-next-line no-unused-vars
const hasOutsideFields = computed(() => fieldsOutsideGrid.value.length > 0);

const labelActionStyle = computed(() => {
  if (!selectedLabel.value) return {};
  
  const buttonSize = 28;
  const scale = visualScale.value || 1;
  
  // Calcular posición escalada del label
  const scaledX = selectedLabel.value.x * scale;
  const scaledY = selectedLabel.value.y * scale;
  
  // Posicionar el botón en la esquina SUPERIOR IZQUIERDA
  // Esto es más consistente porque no depende del ancho del label
  const margin = 4;
  const top = scaledY - buttonSize + margin;
  const left = scaledX - margin;
  
  // Límites para que no se salga del contenedor
  const maxTop = (containerHeight.value || 1115) * scale - buttonSize;
  const maxLeft = (containerWidth.value || 861) * scale - buttonSize;
  
  return {
    top: `${Math.min(Math.max(top, 0), maxTop)}px`,
    left: `${Math.min(Math.max(left, 0), maxLeft)}px`
  };
});

const currentManageList = computed(() => {
  if (manageTab.value === 'inside') return fieldsInsideGrid.value;
  if (manageTab.value === 'outside') return fieldsOutsideGrid.value;
  return deletedLabels.value;
});

// Computed para mostrar información de escala (para usar las variables)
const scaleInfo = computed(() => {
  return {
    scale: visualScale.value?.toFixed(2) || '1.00',
    width: containerWidth.value || 861,
    height: containerHeight.value || 1115,
    scaledWidth: Math.round((containerWidth.value || 861) * (visualScale.value || 1)),
    scaledHeight: Math.round((containerHeight.value || 1115) * (visualScale.value || 1))
  };
});

// Función para actualizar el valor de un campo externo
// eslint-disable-next-line no-unused-vars
const updateExternalFieldValue = (label, event) => {
  label.value = event.target.innerHTML;
};

// Función para manejar el drop con estado de dragging
const handleDropWithState = (event) => {
  isDragging.value = false;
  handleDrop(event);
};

// Función para mover un campo externo de vuelta a la cuadrícula
const moveToGrid = (label) => {
  // Encuentra una posición libre en la cuadrícula
  let newX = 50;
  let newY = 200;

  // Buscar una posición que no esté ocupada
  const occupiedPositions = fieldsInsideGrid.value.map(field => ({ x: field.x, y: field.y }));

  while (occupiedPositions.some(pos => Math.abs(pos.x - newX) < 50 && Math.abs(pos.y - newY) < 30)) {
    newY += 40;
    if (newY > GRID_HEIGHT - 100) {
      newY = 200;
      newX += 100;
    }
  }

  label.x = newX;
  label.y = newY;
};

const isResizeGesture = (event, label) => {
  if (!label?.isInput) return false;
  const el = event.currentTarget;
  if (!el) return false;
  const rect = el.getBoundingClientRect();
  const gutter = 14; // px desde la esquina inferior derecha que reservamos para resize
  const nearRight = rect.right - event.clientX <= gutter;
  const nearBottom = rect.bottom - event.clientY <= gutter;
  return nearRight && nearBottom;
};

const handleLabelMouseDown = (event, label) => {
  selectedLabelId.value = label.id;

  // Si es gesto de resize, se permite el nativo y luego se sincroniza tamaño
  if (isResizeGesture(event, label)) {
    const onUp = () => {
      updateSize(label);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mouseup", onUp);
    return;
  }
  moveLabel(event, label);
};

const handleLabelMouseUp = (label) => {
  updateSize(label);
};

const requestDeleteLabel = (label) => {
  if (!label) return;

  deletedLabels.value.push({
    ...label,
    deletedAt: new Date().toISOString()
  });

  if (pdfData?.value?.labels) {
    pdfData.value.labels = pdfData.value.labels.filter(l => l.id !== label.id);
  }

  selectedLabelId.value = null;
  saveDeletedLabelsToStorage();

  proxy.$notify(
    `Label "${label.id}" movido a la papelera`,
    "",
    "warning",
    "fa-solid fa-circle-info"
  );
};

async function createDefaultTemplate() {
  const doctorId = realPrescriptionData.value.doctor;
  const user = store.state.user;



  if (!doctorId) {
    proxy.$notify("Error: No se encontró ID de doctor", "", "danger", "ni ni-fat-remove ni-lg");
    return;
  }

  try {
    proxy.$notify("Creando plantilla inicial...", "", "info", "ni ni-info-circle ni-lg");

    // Verificar si ya existe un PDF en los datos del doctor
    if (user?.doctor?.prescription_pdf_url) {

      pdfData.value.url = user.doctor.prescription_pdf_url;

      // Si hay parámetros guardados, cargarlos
      if (user.doctor.prescription_pdf_parameters) {
        try {
          const paramsData = user.doctor.prescription_pdf_parameters;
          const params = typeof paramsData === 'string' ? JSON.parse(paramsData) : paramsData;
          if (params && params.labels) {

            pdfData.value.labels = params.labels.map((label, index) => ({
              id: label.id ?? `existing-${index}`,
              text: label.text || "",
              x: label.x || 0,
              y: label.y || 0,
              page: label.page || 1,
              isInput: label.isInput || false,
              width: label.width || 100,
              height: label.height || 20,
              value: label.value || "",
            }));
          } else {
            console.warn('⚠️ No hay labels en prescription_pdf_parameters');
            pdfData.value.labels = [];
          }
        } catch (parseError) {
          console.error('❌ Error al parsear prescription_pdf_parameters:', parseError);
          pdfData.value.labels = [];
        }
      } else {
        console.warn('⚠️ No existe prescription_pdf_parameters en el doctor');
        pdfData.value.labels = [];
      }

      proxy.$notify("¡PDF existente cargado! Ya puedes configurar y generar prescripciones.", "", "success", "ni ni-like-2 ni-lg");
      return;
    }

    // SOLUCIÓN TEMPORAL: Crear plantilla simulada localmente
    const mockTemplateUrl = `/storage/pdfs/doctors/doctor_${doctorId}_prescription.pdf`;

    // 1. Actualizar el objeto pdfData directamente
    pdfData.value.url = mockTemplateUrl;
    // El backend debe proporcionar los labels, no usar defaults locales
    pdfData.value.labels = [];

    // 2. Limpiar localStorage - el servidor maneja los datos
    localStorage.removeItem(`pdfData-doctor`);

    // 3. Forzar actualización del composable
    loadDefaultPdfData();

    proxy.$notify("¡Plantilla temporal creada! Ya puedes configurar y generar prescripciones.", "", "success", "ni ni-like-2 ni-lg");

  } catch (error) {
    // Error al crear plantilla por defecto
    proxy.$notify("Error inesperado al crear la plantilla.", "", "danger", "ni ni-fat-remove ni-lg");
  }
}

function forceOverrideDefaults() {
  localStorage.removeItem("pdfData-doctor");
  // También limpiar la papelera al restablecer valores por defecto
  deletedLabels.value = [];
  localStorage.removeItem('deletedLabels-doctor');
  loadDefaultPdfData();
  proxy.$notify("Parámetros restablecidos y papelera limpiada", "", "success", "ni ni-like-2 ni-lg");
}

// ===== FUNCIONES PARA GESTIÓN DE LABELS ELIMINADOS =====

// Computed para fecha de última eliminación
const lastDeletedDate = computed(() => {
  if (deletedLabels.value.length === 0) return 'Nunca';
  const latest = Math.max(...deletedLabels.value.map(label => new Date(label.deletedAt).getTime()));
  return new Date(latest).toLocaleString('es-ES');
});

// Función para formatear fecha de eliminación
const formatDeletedDate = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 1) return 'Hace 1 día';
  if (diffDays < 7) return `Hace ${diffDays} días`;
  return date.toLocaleDateString('es-ES');
};

// Función para seleccionar/deseleccionar todos los labels
const toggleAllLabels = () => {
  if (selectAllLabels.value) {
    selectedLabelsForDelete.value = (pdfData?.value?.labels || []).map(label => label.id);
  } else {
    selectedLabelsForDelete.value = [];
  }
};

// Función para seleccionar/deseleccionar todos los labels eliminados
const toggleAllDeleted = () => {
  if (selectAllDeleted.value) {
    selectedDeletedLabels.value = deletedLabels.value.map(label => label.id);
  } else {
    selectedDeletedLabels.value = [];
  }
};

// Función para eliminar labels seleccionados
const deleteSelectedLabels = () => {
  if (selectedLabelsForDelete.value.length === 0) return;

  const labelsToDelete = (pdfData?.value?.labels || []).filter(label =>
    selectedLabelsForDelete.value.includes(label.id)
  );

  // Agregar timestamp de eliminación y mover a papelera
  labelsToDelete.forEach(label => {
    deletedLabels.value.push({
      ...label,
      deletedAt: new Date().toISOString()
    });
  });

  // Remover de la lista principal
  if (pdfData?.value?.labels) {
    pdfData.value.labels = pdfData.value.labels.filter(label =>
      !selectedLabelsForDelete.value.includes(label.id)
    );
  }

  // Limpiar selección y cerrar modal
  selectedLabelsForDelete.value = [];
  selectAllLabels.value = false;
  showDeleteModal.value = false;

  // Guardar cambios en localStorage (no necesita sincronizar porque ya se removieron)
  saveDeletedLabelsToStorage();

  proxy.$notify(
    `${labelsToDelete.length} label(s) movido(s) a la papelera`,
    "",
    "warning",
    "ni ni-basket ni-lg"
  );
};// Función para restaurar un label individual
const restoreLabel = (deletedLabel) => {
  // Crear una copia sin el timestamp de eliminación
  const restoredLabel = { ...deletedLabel };
  delete restoredLabel.deletedAt;

  // Verificar que no exista ya en la lista principal
  const existingIds = (pdfData?.value?.labels || []).map(l => l.id);
  if (!existingIds.includes(restoredLabel.id)) {
    // Agregar de vuelta a la lista principal
    if (pdfData?.value?.labels) {
      pdfData.value.labels.push(restoredLabel);
    } else if (pdfData?.value) {
      pdfData.value.labels = [restoredLabel];
    }
  }

  // Remover de la papelera
  deletedLabels.value = deletedLabels.value.filter(label => label.id !== deletedLabel.id);

  // Guardar cambios
  saveDeletedLabelsToStorage();

  proxy.$notify(
    `Label "${deletedLabel.id}" restaurado exitosamente`,
    "",
    "success",
    "ni ni-like-2 ni-lg"
  );
};

// Función para restaurar labels seleccionados
const restoreSelectedLabels = () => {
  if (selectedDeletedLabels.value.length === 0) return;

  const labelsToRestore = deletedLabels.value.filter(label =>
    selectedDeletedLabels.value.includes(label.id)
  );

  // Restaurar cada label (verificar que no exista ya)
  labelsToRestore.forEach(deletedLabel => {
    const restoredLabel = { ...deletedLabel };
    delete restoredLabel.deletedAt;

    // Verificar que no exista ya en la lista principal
    const existingIds = (pdfData?.value?.labels || []).map(l => l.id);
    if (!existingIds.includes(restoredLabel.id)) {
      if (pdfData?.value?.labels) {
        pdfData.value.labels.push(restoredLabel);
      } else if (pdfData?.value) {
        pdfData.value.labels = [restoredLabel];
      }
    }
  });

  // Remover de la papelera
  deletedLabels.value = deletedLabels.value.filter(label =>
    !selectedDeletedLabels.value.includes(label.id)
  );

  // Limpiar selección
  selectedDeletedLabels.value = [];
  selectAllDeleted.value = false;

  // Guardar cambios
  saveDeletedLabelsToStorage();

  proxy.$notify(
    `${labelsToRestore.length} label(s) restaurado(s) exitosamente`,
    "",
    "success",
    "ni ni-like-2 ni-lg"
  );
};// Función para eliminación permanente individual
const permanentDelete = (deletedLabel) => {
  if (confirm(`¿Estás seguro de que quieres eliminar permanentemente el label "${deletedLabel.id}"? Esta acción no se puede deshacer.`)) {
    deletedLabels.value = deletedLabels.value.filter(label => label.id !== deletedLabel.id);
    saveDeletedLabelsToStorage();

    proxy.$notify(
      `Label "${deletedLabel.id}" eliminado permanentemente`,
      "",
      "info",
      "ni ni-basket ni-lg"
    );
  }
};

// Función para eliminación permanente de seleccionados
const permanentDeleteSelected = () => {
  if (selectedDeletedLabels.value.length === 0) return;

  if (confirm(`¿Estás seguro de que quieres eliminar permanentemente ${selectedDeletedLabels.value.length} label(s)? Esta acción no se puede deshacer.`)) {
    const deletedCount = selectedDeletedLabels.value.length;

    deletedLabels.value = deletedLabels.value.filter(label =>
      !selectedDeletedLabels.value.includes(label.id)
    );

    selectedDeletedLabels.value = [];
    selectAllDeleted.value = false;
    saveDeletedLabelsToStorage();

    proxy.$notify(
      `${deletedCount} label(s) eliminado(s) permanentemente`,
      "",
      "info",
      "ni ni-basket ni-lg"
    );
  }
};

// Función para vaciar toda la papelera
const clearTrash = () => {
  if (confirm('¿Estás seguro de que quieres vaciar completamente la papelera? Esta acción no se puede deshacer.')) {
    const deletedCount = deletedLabels.value.length;
    deletedLabels.value = [];
    selectedDeletedLabels.value = [];
    selectAllDeleted.value = false;
    showTrashModal.value = false;

    saveDeletedLabelsToStorage();

    proxy.$notify(
      `Papelera vaciada - ${deletedCount} label(s) eliminado(s) permanentemente`,
      "",
      "info",
      "ni ni-basket ni-lg"
    );
  }
};

// Función para guardar/cargar papelera en localStorage
const saveDeletedLabelsToStorage = () => {
  localStorage.setItem('deletedLabels-doctor', JSON.stringify(deletedLabels.value));
};

const loadDeletedLabelsFromStorage = () => {
  try {
    const stored = localStorage.getItem('deletedLabels-doctor');
    if (stored) {
      deletedLabels.value = JSON.parse(stored);
    }
  } catch (error) {
    console.error('Error loading deleted labels from storage:', error);
    deletedLabels.value = [];
  }
};

// Cargar labels eliminados al inicializar
loadDeletedLabelsFromStorage();

// Variable para prevenir bucles infinitos
let isSyncing = false;

// Función de sincronización mejorada que previene bucles
const syncLabelsWithDeletedSafe = () => {
  if (isSyncing || !pdfData?.value?.labels) return;

  isSyncing = true;
  try {
    const deletedIds = deletedLabels.value.map(label => label.id);
    const originalLength = pdfData.value.labels.length;
    pdfData.value.labels = pdfData.value.labels.filter(label => !deletedIds.includes(label.id));

    // Solo log si realmente se removieron labels
    if (originalLength !== pdfData.value.labels.length) {
      console.log('Labels sincronizados con papelera');
    }
  } finally {
    isSyncing = false;
  }
};

// Watcher simplificado solo para cuando se restauran labels desde papelera
watch(() => deletedLabels.value.length, (newLength, oldLength) => {
  // Solo sincronizar cuando se restauran labels (la papelera se reduce)
  if (newLength < oldLength) {
    // Pequeño delay para evitar conflictos
    setTimeout(() => {
      syncLabelsWithDeletedSafe();
    }, 10);
  }
});

// Sincronización inicial solo una vez
setTimeout(() => {
  syncLabelsWithDeletedSafe();
}, 100);

</script>

<style>
.pdf-labels-content {
  padding: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
}

/* Forzar centrado global */
.pdf-labels-content * {
  box-sizing: border-box;
}

.manage-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 12px;
}

.tab-btn {
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  color: #475569;
  padding: 8px 12px;
  border-radius: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: all 0.2s ease;
}

.tab-btn .pill {
  background: #e2e8f0;
  color: #0f172a;
  border-radius: 999px;
  padding: 2px 8px;
  font-size: 12px;
}

.tab-btn .pill.warning {
  background: #fff2cc;
  color: #b45309;
}

.tab-btn .pill.danger {
  background: #fde2e4;
  color: #b91c1c;
}

.tab-btn.active {
  background: linear-gradient(135deg, #e0f2fe, #e0f7ff);
  border-color: #38bdf8;
  color: #0ea5e9;
  box-shadow: 0 4px 12px rgba(14, 165, 233, 0.2);
}

.manage-section {
  border: 1px solid #e9ecef;
  border-radius: 12px;
  padding: 12px;
  background: #fff;
}

.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.badge-count {
  background: #e9ecef;
  color: #1f2937;
  border-radius: 10px;
  padding: 4px 8px;
  font-size: 12px;
  font-weight: 700;
}

.badge-count.warning {
  background: #fff2cc;
  color: #b45309;
}

.badge-count.danger {
  background: #fde2e4;
  color: #b91c1c;
}

.manage-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.manage-item {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  padding: 10px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  background: #f9fafb;
}

.manage-meta strong {
  display: block;
}

.manage-actions {
  display: flex;
  gap: 6px;
}

.empty-row {
  border: 1px dashed #e5e7eb;
  border-radius: 10px;
  padding: 14px;
  text-align: center;
  color: #6b7280;
  background: #f8fafc;
}

.simple-scroll {
  max-height: 360px;
  overflow-y: auto;
}

.action-toolbar {
  max-width: 900px;
  width: 92%;
  margin-left: auto;
  margin-right: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  align-items: center;
  padding: 12px 14px;
  border-radius: 12px;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.04);
}

.action-buttons {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 10px;
}

.toolbar-stats {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  border: 1px solid #e9ecef;
  background: #f8f9fa;
  color: #495057;
}

.status-chip.success {
  background: #e6f4ea;
  color: #198754;
  border-color: #c8e6c9;
}

.status-chip.warning {
  background: #fff4e5;
  color: #b26a00;
  border-color: #ffd8a8;
}

.status-chip.danger {
  background: #fde2e4;
  color: #b02a37;
  border-color: #f8d7da;
}

.status-chip.neutral {
  background: #eef2f7;
  color: #516173;
  border-color: #e1e7ef;
}

.status-cards {
  width: 100%;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(260px, 1fr));
  gap: 12px;
  margin: 12px 0 4px;
}

.status-card {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  background: #ffffff;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.status-card strong {
  display: block;
  color: #212529;
  margin-bottom: 2px;
}

.status-card p {
  margin: 0;
  color: #6c757d;
  font-size: 13px;
  line-height: 1.35;
}

.card-icon {
  width: 40px;
  height: 40px;
  border-radius: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: white;
}

.card-icon.ready {
  background: linear-gradient(135deg, #52b788, #2d6a4f);
}

.card-icon.warning {
  background: linear-gradient(135deg, #ffb347, #ff8c42);
}

.card-icon.neutral {
  background: linear-gradient(135deg, #6c63ff, #4c47e6);
}

.card-icon.muted {
  opacity: 0.5;
}

.pdf-workspace {
  display: flex;
  gap: 20px;
  justify-content: center;
  flex-wrap: wrap;
  padding: 20px;
  max-width: 100%;
  overflow-x: auto;
  align-items: flex-start;
  min-height: 400px;
  width: 100%;
}

.pdf-viewer {
  position: relative;
  width: 861px;
  height: 1115px;
  border: 1px solid #ccc;
  flex-shrink: 0;
  transform-origin: center center;
  display: flex;
  justify-content: center;
  align-items: center;
  background: white;
  margin: 0 auto;
}

.pdf-viewer-container {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: 0 auto;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 18px;
  background: #ffffff;
  overflow: visible;
  box-sizing: border-box;
  min-height: 300px;
  position: relative;
  width: fit-content;
  max-width: 100%;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.06);
}

.scale-info {
  position: absolute;
  top: 8px;
  right: 8px;
  background: rgba(0, 0, 0, 0.7);
  color: white;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 11px;
  z-index: 10;
  pointer-events: none;
}

.external-fields-zone {
  position: relative;
  min-width: 300px;
  max-width: 400px;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  background-color: #f8f9fa;
  padding: 16px;
  height: fit-content;
  max-height: 600px;
  overflow-y: auto;
}

.external-fields-header {
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #dee2e6;
}

.external-fields-header h4 {
  margin: 0 0 4px 0;
  color: #495057;
  font-size: 18px;
}

.external-fields-header small {
  color: #6c757d;
  font-style: italic;
}

.external-fields-container {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.external-field-item {
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 6px;
  padding: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
}

.external-field-label {
  font-size: 14px;
  color: #495057;
  margin-bottom: 8px;
}

.external-field-input {
  margin-bottom: 8px;
}

.external-field-editor {
  border: 1px solid #ced4da;
  border-radius: 4px;
  padding: 8px;
  min-height: 40px;
  background: white;
  font-size: 13px;
  line-height: 1.4;
  text-align: left;
}

.external-field-editor:focus {
  outline: none;
  border-color: #80bdff;
  box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, .25);
}

.external-field-value {
  background: #e9ecef;
  border-radius: 4px;
  padding: 8px;
  font-size: 13px;
  color: #495057;
  margin-bottom: 8px;
}

.external-field-position {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #6c757d;
}

.external-field-position small {
  margin: 0;
}

.external-field-position .btn {
  font-size: 11px;
  padding: 4px 8px;
  transition: all 0.2s ease;
}

.external-field-position .btn:hover {
  transform: translateX(-2px);
}

.text-warning {
  color: #ffc107 !important;
}

.text-muted {
  color: #6c757d !important;
}

/* Indicador visual para campos fuera de la cuadrícula */
.external-fields-zone::before {
  content: '';
  position: absolute;
  top: -5px;
  left: -5px;
  right: -5px;
  bottom: -5px;
  border: 2px dashed #ffc107;
  border-radius: 12px;
  pointer-events: none;
  opacity: 0.6;
}

.all-fields-inside-info {
  min-width: 300px;
  max-width: 400px;
  border: 1px solid #d4edda;
  border-radius: 8px;
  background-color: #f8fff9;
  padding: 24px 16px;
}

.text-success {
  color: #198754 !important;
}

.all-fields-inside-info h5 {
  margin: 8px 0 4px 0;
}

@media (max-width: 1400px) {
  .pdf-viewer-container {
    padding: 15px;
  }
}

@media (max-width: 1200px) {
  .pdf-workspace {
    flex-direction: column;
    align-items: center;
    padding: 15px;
    justify-content: center;
  }

  .pdf-viewer-container {
    max-width: 95vw;
    padding: 15px;
    margin: 0 auto;
    width: auto;
    left: 50%;
    transform: translateX(-50%);
    position: relative;
  }

  .external-fields-zone {
    max-width: 90vw;
    min-width: 300px;
  }
}

@media (max-width: 900px) {
  .pdf-workspace {
    padding: 10px;
    justify-content: center;
  }

  .pdf-viewer-container {
    max-width: 95vw;
    margin: 0 auto;
    border: 1px solid #ddd;
    padding: 10px;
    background-color: #f8f9fa;
    width: auto;
    justify-content: center;
    left: 50%;
    transform: translateX(-50%);
    position: relative;
  }

  .pdf-viewer {
    border: 1px solid #ccc;
    margin: 0 auto;
  }
}

/* Capa para la cuadrícula */
.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 861px;
  height: 1115px;
  pointer-events: none;
  display: grid;
  grid-template-columns: repeat(20, 1fr);
  grid-template-rows: repeat(25, 1fr);
}

.grid-overlay div {
  border: 0.5px solid rgba(128, 128, 128, 0.2);
}

/* Capa para las etiquetas */
.overlay {
  position: absolute;
  top: 0;

  overflow: auto;
}

canvas {
  display: block;
  width: 861px;
  height: 1115px;
  margin: 0 auto;
  position: absolute;
  top: 0;
  left: 0;
}

.overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 861px;
  height: 1115px;
}

.label,
.field {
  position: absolute;
  font-size: 15px;
  font-weight: bold;
  background: transparent;
  pointer-events: auto;
}

.field {
  resize: both;
  overflow: auto;
  text-align: left;
  min-width: 60px;
  min-height: 24px;
  padding: 4px 6px;
  border: 1px dashed rgba(0, 123, 255, 0.4);
  border-radius: 4px;
  background: transparent;
}

.label.selected,
.field.selected {
  outline: 2px solid rgba(0, 123, 255, 0.6);
  outline-offset: 2px;
  border-color: rgba(0, 123, 255, 0.6);
}

.label-actions-floating {
  position: absolute;
  z-index: 20;
  transform: translate(-50%, -50%);
}

.close-chip {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  border: 1px solid #ff2d55;
  background: #ffffff;
  color: #ff2d55;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
  box-shadow: 0 2px 8px rgba(255, 45, 85, 0.25);
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
}

.close-chip:hover {
  transform: translateY(-2px) scale(1.05);
  box-shadow: 0 4px 12px rgba(255, 45, 85, 0.35);
  background: #fff5f7;
  border-color: #ff1744;
  color: #ff1744;
}

.close-chip:active {
  transform: translateY(0) scale(0.98);
  box-shadow: 0 1px 4px rgba(255, 45, 85, 0.2);
}

.info-card {
  display: flex;
  align-items: center;
  gap: 20px;
  padding: 24px 28px;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  margin-top: 24px;
  margin-bottom: 24px;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
  width: 92%;
}

.info-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: #f3f4f6;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
  color: #6b7280;
}

.info-content {
  flex: 1;
}

.info-title {
  margin: 0 0 6px 0;
  font-size: 16px;
  font-weight: 600;
  color: #374151;
}

.info-description {
  margin: 0;
  font-size: 14px;
  color: #6b7280;
  line-height: 1.5;
}

.success-info .info-icon {
  background: #f3f4f6;
  color: #6b7280;
}

.success-info .info-title {
  color: #374151;
}

.upload-container {
  margin: 22px auto;
  border: 2px dashed #cbd5e1;
  border-radius: 14px;
  max-width: 900px;
  width: 92%;
  min-width: 300px;
  padding: 32px 22px;
  cursor: pointer;
  background: #f8fafc;
  transition: all 0.25s ease;
  box-shadow: none;
}

.upload-container:hover {
  border-color: #94a3b8;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.06);
}

.upload-label {
  display: block;
  cursor: pointer;
  width: 100%;
  height: 100%;
}

.upload-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 14px;
}

.file-input {
  display: none;
}

.upload-icon {
  font-size: 48px;
  color: #0f172a;
  margin-bottom: 8px;
}

.upload-text {
  text-align: center;
}

.upload-text strong {
  font-size: 18px;
  color: #111827;
  display: block;
  margin-bottom: 8px;
}

.upload-text p {
  margin: 0;
  color: #475569;
  font-size: 14px;
}

.selected-file {
  background: #ecfdf3;
  border: 1px solid #bbf7d0;
  border-radius: 10px;
  padding: 12px 16px;
  color: #166534;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  margin-top: 12px;
}

.upload-container.dragover {
  border-color: #0ea5e9;
  background: #eef2ff;
}

@media (max-width: 900px) {
  .upload-container {
    width: 95%;
    margin: 20px auto;
  }
}

@media (max-width: 600px) {
  .pdf-labels-content {
    padding: 5px;
  }

  .pdf-workspace {
    padding: 5px;
    gap: 15px;
  }

  .pdf-viewer-container {
    max-width: 100vw;
    padding: 5px;
    border: none;
    background-color: transparent;
    left: 50%;
    transform: translateX(-50%);
    position: relative;
  }

  .pdf-viewer {
    margin: 0 auto;
    border: 2px solid #007bff;
    border-radius: 8px;
  }

  .upload-container {
    width: 98%;
    margin: 15px auto;
    padding: 20px 10px;
  }
}

/* ===== ESTILOS PARA MODALES MODERNOS ===== */

.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }

  to {
    opacity: 1;
  }
}

.modern-modal {
  background: white;
  width: 90%;
  max-width: 600px;
  max-height: 80vh;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  animation: slideUp 0.3s ease-out;
  position: relative;
}

.large-modal {
  max-width: 900px;
  max-height: 85vh;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

.modal-header {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: white;
  padding: 20px 24px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  border-bottom: none;
  position: relative;
}

.modal-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  display: flex;
  align-items: center;
}

.close-btn {
  background: rgba(255, 255, 255, 0.2);
  border: none;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.modal-body {
  padding: 24px;
  overflow-y: auto;
  flex: 1;
}

.modal-footer {
  padding: 16px 24px;
  border-top: 1px solid #e9ecef;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  background: #f8f9fa;
}

/* Estilos para listas de labels */
.labels-list,
.deleted-labels-list {
  margin-top: 16px;
}

.label-selection-header,
.deleted-selection-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: #f8f9fa;
  border-radius: 8px;
  margin-bottom: 16px;
  border: 1px solid #e9ecef;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 8px;
  font-weight: 500;
  cursor: pointer;
  margin: 0;
}

.checkbox-label input[type="checkbox"] {
  width: 18px;
  height: 18px;
  accent-color: #007bff;
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.labels-grid,
.deleted-labels-grid {
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-height: 400px;
  overflow-y: auto;
  padding: 4px;
}

.label-item,
.deleted-label-item {
  border: 2px solid #e9ecef;
  border-radius: 10px;
  background: white;
  transition: all 0.2s ease;
  cursor: pointer;
}

.label-item:hover,
.deleted-label-item:hover {
  border-color: #007bff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.15);
  transform: translateY(-1px);
}

.label-item.selected,
.deleted-label-item.selected {
  border-color: #007bff;
  background: #f0f7ff;
  box-shadow: 0 4px 12px rgba(0, 123, 255, 0.2);
}

.label-checkbox,
.deleted-label-checkbox {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  cursor: pointer;
  margin: 0;
  width: 100%;
}

.label-checkbox input[type="checkbox"],
.deleted-label-checkbox input[type="checkbox"] {
  width: 20px;
  height: 20px;
  margin-top: 2px;
  accent-color: #007bff;
  flex-shrink: 0;
}

.label-info,
.deleted-label-info {
  flex: 1;
  min-width: 0;
}

.label-name {
  display: flex;
  align-items: center;
  margin-bottom: 6px;
  font-weight: 600;
  color: #343a40;
}

.label-details,
.deleted-label-details {
  display: flex;
  flex-direction: column;
  gap: 4px;
  margin-bottom: 8px;
}

.label-text {
  color: #6c757d;
  font-size: 14px;
  font-style: italic;
}

.label-position {
  color: #adb5bd;
  font-size: 12px;
  font-family: 'Courier New', monospace;
}

/* Estilos específicos para labels eliminados */
.deleted-label-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 8px;
}

.deleted-date {
  color: #6c757d;
  font-size: 12px;
  text-align: right;
}

.deleted-label-actions {
  display: flex;
  gap: 8px;
  margin-top: 12px;
}

.deleted-label-actions .btn {
  padding: 6px 12px;
  font-size: 12px;
  border-radius: 6px;
}

/* Estadísticas de la papelera */
.trash-stats {
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8f9fa;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #6c757d;
  font-size: 14px;
}

.stat-item i {
  width: 16px;
  text-align: center;
}

/* Estado vacío de la papelera */
.empty-trash {
  text-align: center;
  padding: 60px 20px;
  color: #6c757d;
}

.empty-trash h4 {
  color: #6c757d;
  margin-bottom: 8px;
}

/* Alertas personalizadas */
.alert {
  padding: 12px 16px;
  border-radius: 8px;
  border: none;
  display: flex;
  align-items: center;
}

.alert-warning {
  background: #fff3cd;
  color: #856404;
  border-left: 4px solid #ffc107;
}

/* Botones personalizados */
.btn {
  padding: 8px 16px;
  border-radius: 6px;
  border: 1px solid transparent;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  text-decoration: none;
  font-size: 14px;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background: #007bff;
  color: white;
  border-color: #007bff;
}

.btn-primary:hover:not(:disabled) {
  background: #0056b3;
  border-color: #0056b3;
  transform: translateY(-1px);
}

.btn-success {
  background: #28a745;
  color: white;
  border-color: #28a745;
}

.btn-success:hover:not(:disabled) {
  background: #1e7e34;
  border-color: #1e7e34;
}

.btn-danger {
  background: #dc3545;
  color: white;
  border-color: #dc3545;
}

.btn-danger:hover:not(:disabled) {
  background: #bd2130;
  border-color: #bd2130;
}

.btn-warning {
  background: #ffc107;
  color: #212529;
  border-color: #ffc107;
}

.btn-warning:hover:not(:disabled) {
  background: #e0a800;
  border-color: #e0a800;
}

.btn-outline-success {
  background: transparent;
  color: #28a745;
  border-color: #28a745;
}

.btn-outline-success:hover:not(:disabled) {
  background: #28a745;
  color: white;
}

.btn-outline-danger {
  background: transparent;
  color: #dc3545;
  border-color: #dc3545;
}

.btn-outline-danger:hover:not(:disabled) {
  background: #dc3545;
  color: white;
}

.btn-sm {
  padding: 6px 12px;
  font-size: 12px;
}

/* Botón de cerrar del modal - estilo BaseModal */
.modal-btn-close {
  padding: 0.875rem 1.75rem;
  border-radius: 12px;
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
  border: 2px solid #e2e8f0;
  background: white;
  color: #334155;
}

.modal-btn-close:hover {
  background: #667eea;
  border-color: #667eea;
  color: white;
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(102, 126, 234, 0.4);
}

/* Responsive para modales */
@media (max-width: 768px) {
  .modern-modal {
    width: 95%;
    max-height: 90vh;
    margin: 20px;
  }

  .large-modal {
    max-width: 95%;
  }

  .modal-header {
    padding: 16px 20px;
  }

  .modal-body {
    padding: 20px;
  }

  .labels-grid,
  .deleted-labels-grid {
    max-height: 300px;
  }

  .trash-stats {
    flex-direction: column;
    gap: 12px;
  }

  .label-selection-header,
  .deleted-selection-header {
    flex-direction: column;
    gap: 12px;
    align-items: stretch;
  }

  .action-buttons {
    justify-content: center;
  }
}

@media (max-width: 480px) {
  .modal-header h3 {
    font-size: 18px;
  }

  .label-checkbox,
  .deleted-label-checkbox {
    padding: 12px;
  }

  .deleted-label-actions {
    flex-direction: column;
  }

  .deleted-label-actions .btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
